import React, { useCallback, useEffect, useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import type { ArchivedSeasonSummary, RosterPlayerHistory } from '@shootout-comps/core';
import type { League, LeagueFormat, TeamStanding, Fixture, ArchivedSeason, WinLeaderEntry, NotableBreak } from '@shootout-comps/types';
import { useLang } from '@shootout-comps/i18n';
import { SnookerBallSpinner } from '../../components/SnookerBall';
import DivisionTabs from '../../components/DivisionTabs';
import FixtureCalendar from '../../components/FixtureCalendar';
import { isSameLocalDate } from '../../utils/fixtureDate';

interface PublicLeagueDetailPageProps {
  leagueId: string;
  onBack: () => void;
  onSpectateMatch: (leagueId: string, matchId: string) => void;
}

const FORMAT_LABEL: Record<LeagueFormat, string> = { handicap: '🔴 Handicap', scratch: '🎯 Scratch' };

// Read-only counterpart to LeagueDashboardPage.tsx/DivisionTeamsPage.tsx/DivisionFixturesPage.tsx/
// DivisionStandingsPage.tsx for a public league — no management controls, just browsing +
// jumping into a live/complete match to spectate (PublicLeagueFramePage.tsx).
export default function PublicLeagueDetailPage({ leagueId, onBack, onSpectateMatch }: PublicLeagueDetailPageProps) {
  const { fetchPublicLeague, fetchPublicLeagueStandings, fetchPublicSeasons, fetchPublicSeason, fetchPublicRosterPlayerHistory, fetchPublicWinLeaders, fetchPublicBreakLeaders } = useLeague();
  const { t } = useLang();
  const [league, setLeague] = useState<League | null>(null);
  const [error, setError] = useState('');
  const [divisionId, setDivisionId] = useState<string | null>(null);
  const [tab, setTab] = useState<'standings' | 'fixtures' | 'teams' | 'history'>('standings');
  const [standings, setStandings] = useState<TeamStanding[] | null>(null);
  const [winLeaders, setWinLeaders] = useState<WinLeaderEntry[] | null>(null);
  const [breakLeaders, setBreakLeaders] = useState<NotableBreak[] | null>(null);
  const [expandedFixtureId, setExpandedFixtureId] = useState<string | null>(null);
  const [fixturesViewMode, setFixturesViewMode] = useState<'list' | 'calendar'>('list');
  const [calendarSelectedDate, setCalendarSelectedDate] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [playerHistory, setPlayerHistory] = useState<RosterPlayerHistory | null>(null);
  const [seasons, setSeasons] = useState<ArchivedSeasonSummary[] | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<ArchivedSeason | null>(null);

  useEffect(() => {
    fetchPublicLeague(leagueId)
      .then(l => { setLeague(l); if (l.divisions.length > 0) setDivisionId(l.divisions[0]._id); })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load league'));
  }, [fetchPublicLeague, leagueId]);

  const division = league?.divisions.find(d => d._id === divisionId);

  const reloadStandings = useCallback(() => {
    if (!divisionId) return;
    setStandings(null);
    fetchPublicLeagueStandings(leagueId, divisionId).then(setStandings).catch(() => setStandings([]));
  }, [fetchPublicLeagueStandings, leagueId, divisionId]);

  useEffect(() => { if (tab === 'standings') reloadStandings(); }, [tab, reloadStandings]);

  useEffect(() => {
    if (tab !== 'standings' || !divisionId) return;
    setWinLeaders(null);
    fetchPublicWinLeaders(leagueId, divisionId).then(setWinLeaders).catch(() => setWinLeaders([]));
    setBreakLeaders(null);
    fetchPublicBreakLeaders(leagueId, divisionId).then(setBreakLeaders).catch(() => setBreakLeaders([]));
  }, [tab, divisionId, leagueId, fetchPublicWinLeaders, fetchPublicBreakLeaders]);

  useEffect(() => {
    if (tab !== 'history' || !divisionId) return;
    setSeasons(null); setSelectedSeason(null);
    fetchPublicSeasons(leagueId, divisionId).then(setSeasons).catch(() => setSeasons([]));
  }, [tab, divisionId, leagueId, fetchPublicSeasons]);

  const openPlayer = async (teamId: string, playerId: string) => {
    if (!divisionId) return;
    setSelectedPlayerId(playerId);
    setPlayerHistory(null);
    try {
      setPlayerHistory(await fetchPublicRosterPlayerHistory(leagueId, divisionId, teamId, playerId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load player history');
    }
  };

  const openSeason = async (seasonId: string) => {
    if (!divisionId) return;
    try {
      setSelectedSeason(await fetchPublicSeason(leagueId, divisionId, seasonId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load season');
    }
  };

  if (error) return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{error}</div>;
  if (!league) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}><SnookerBallSpinner size={32} /></div>;

  const th: React.CSSProperties = { textAlign: 'right', padding: '0.5rem 0.6rem', fontSize: '0.68rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 };
  const td: React.CSSProperties = { textAlign: 'right', padding: '0.55rem 0.6rem', fontSize: '0.85rem' };

  const fixtureRow = (f: Fixture) => {
    const homeTeam = division?.teams.find(t => t._id === f.homeTeamId);
    const awayTeam = division?.teams.find(t => t._id === f.awayTeamId);
    return (
      <div key={f._id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', marginBottom: '0.5rem', overflow: 'hidden' }}>
        <div onClick={() => setExpandedFixtureId(expandedFixtureId === f._id ? null : f._id)} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', cursor: 'pointer' }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: '0.88rem' }}>
            {f.homeTeamName} <span style={{ color: 'var(--chalk-dim)' }}>vs</span> {f.awayTeamName}
            {f.status === 'complete' && <span style={{ color: 'var(--gold)', fontWeight: 700, marginLeft: '0.5rem' }}>{f.homeFrameWins}–{f.awayFrameWins}</span>}
          </div>
          <span style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', textTransform: 'uppercase' }}>{f.status.replace('_', ' ')}</span>
        </div>
        {expandedFixtureId === f._id && (
          <div style={{ padding: '0 1rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {f.frameMatches.map((fm, i) => {
              const clickable = fm.status === 'active' || fm.status === 'complete';
              const homeName = homeTeam?.roster.find(p => p._id === fm.player1?._id)?.name ?? fm.player1?.name ?? '?';
              const awayName = awayTeam?.roster.find(p => p._id === fm.player2?._id)?.name ?? fm.player2?.name ?? '?';
              return (
                <div
                  key={fm._id}
                  onClick={() => clickable && onSpectateMatch(leagueId, fm._id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem', padding: '0.4rem 0.6rem', background: 'rgba(255,255,255,0.02)', borderRadius: '6px', cursor: clickable ? 'pointer' : 'default', color: clickable ? 'var(--chalk)' : 'var(--chalk-dim)' }}
                >
                  <span style={{ color: 'var(--chalk-dim)', width: '16px' }}>{i + 1}.</span>
                  <span style={{ flex: 1 }}>{homeName} vs {awayName}</span>
                  {fm.status === 'complete' && <span style={{ color: 'var(--gold)', fontWeight: 700 }}>{fm.score1}–{fm.score2}</span>}
                  {fm.status === 'active' && <span style={{ color: '#2ecc71', fontWeight: 700 }}>👁 Watch</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
        {t.backToPublicLeagues}
      </button>

      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.6rem,5vw,2.2rem)', color: 'var(--gold)', marginBottom: '0.75rem' }}>
        🏆 {league.name}
      </h2>

      {league.divisions.length > 1 && (
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {league.divisions.map(d => (
            <button key={d._id} onClick={() => { setDivisionId(d._id); setSelectedTeamId(null); setSelectedPlayerId(null); setPlayerHistory(null); }} style={{ background: divisionId === d._id ? 'rgba(201,168,76,0.15)' : 'transparent', border: `1px solid ${divisionId === d._id ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.12)'}`, borderRadius: '20px', padding: '0.35rem 0.8rem', fontSize: '0.78rem', color: divisionId === d._id ? 'var(--gold)' : 'var(--chalk-dim)', cursor: 'pointer' }}>
              {d.name}
            </button>
          ))}
        </div>
      )}

      {division && (
        <>
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{FORMAT_LABEL[division.format]}</p>
          <DivisionTabs active={tab} onSelect={t2 => { setTab(t2); setSelectedTeamId(null); setSelectedPlayerId(null); setPlayerHistory(null); }} />

          {tab === 'standings' && (
            <>
            {selectedTeamId ? (() => {
              const team = division.teams.find(tm => tm._id === selectedTeamId);
              if (!team) return null;

              if (selectedPlayerId) {
                if (!playerHistory) return <div style={{ textAlign: 'center', padding: '2rem' }}><SnookerBallSpinner size={24} /></div>;
                const played = playerHistory.matches.length;
                const won = playerHistory.matches.filter(m => m.won === true).length;
                const lost = playerHistory.matches.filter(m => m.won === false).length;
                const careerHighBreak = playerHistory.matches.reduce((max, m) => Math.max(max, m.highBreak), 0);
                return (
                  <>
                    <button onClick={() => { setSelectedPlayerId(null); setPlayerHistory(null); }} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
                      ← {team.name}
                    </button>
                    <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.3rem,4.5vw,1.7rem)', color: 'var(--gold)', marginBottom: '0.2rem' }}>{playerHistory.player.name}</h3>
                    <p style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem', marginBottom: '1.25rem' }}>hcp {playerHistory.player.handicap}</p>

                    <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                      {([['Played', played], ['Won', won], ['Lost', lost], ['High Break', careerHighBreak]] as const).map(([label, value]) => (
                        <div key={label} style={{ textAlign: 'center' }}>
                          <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', color: 'var(--gold)', lineHeight: 1 }}>{value}</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.2rem' }}>{label}</div>
                        </div>
                      ))}
                    </div>

                    {playerHistory.matches.length === 0 ? (
                      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>No completed matches yet.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {playerHistory.matches.map(m => (
                          <div key={m.frameMatchId} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.75rem 1rem' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>
                                vs {m.opponentName} <span style={{ color: 'var(--chalk-dim)', fontWeight: 400 }}>({m.opponentTeamName}{m.isHome ? '' : ' — away'})</span>
                              </div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginTop: '0.2rem' }}>
                                {m.completedAt ? new Date(m.completedAt).toLocaleDateString() : m.scheduledAt ? new Date(m.scheduledAt).toLocaleDateString() : ''}
                                {m.highBreak > 0 ? ` · High break: ${m.highBreak}` : ''}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.1rem', color: m.won === true ? '#2ecc71' : m.won === false ? '#e74c3c' : 'var(--chalk-dim)' }}>
                                {m.scoreFor ?? '—'}–{m.scoreAgainst ?? '—'}
                              </div>
                              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: m.won === true ? '#2ecc71' : m.won === false ? '#e74c3c' : 'var(--chalk-dim)', textTransform: 'uppercase' }}>
                                {m.won === true ? 'Won' : m.won === false ? 'Lost' : ''}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              }

              const results = division.fixtures.filter(f => f.homeTeamId === selectedTeamId || f.awayTeamId === selectedTeamId);
              return (
                <>
                  <button onClick={() => setSelectedTeamId(null)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
                    ← Standings
                  </button>
                  <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.3rem,4.5vw,1.7rem)', color: 'var(--gold)', marginBottom: '0.9rem' }}>{team.name}</h3>

                  <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>Players</div>
                  {team.roster.length === 0 ? (
                    <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', padding: '0.5rem 0' }}>No players yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.5rem' }}>
                      {team.roster.map(p => (
                        <span key={p._id} onClick={() => openPlayer(team._id, p._id)} style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '0.3rem 0.75rem', cursor: 'pointer' }}>
                          {team.captainPlayerId === p._id ? '⭐ ' : ''}{p.name} <span style={{ color: 'var(--chalk-dim)' }}>· hcp {p.handicap ?? 0}</span>
                        </span>
                      ))}
                    </div>
                  )}

                  <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>Results</div>
                  {results.length === 0 ? (
                    <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', padding: '0.5rem 0' }}>No fixtures yet.</p>
                  ) : results.map(fixtureRow)}
                </>
              );
            })() : standings === null ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}><SnookerBallSpinner size={24} /></div>
            ) : standings.length === 0 ? (
              <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>{t.noTeamsInDivision}</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <th style={{ ...th, textAlign: 'left' }}>#</th>
                      <th style={{ ...th, textAlign: 'left' }}>{t.standingsTeamCol}</th>
                      <th style={th}>P</th><th style={th}>W</th><th style={th}>D</th><th style={th}>L</th>
                      <th style={th}>F</th><th style={th}>A</th><th style={th}>+/-</th>
                      <th style={{ ...th, color: 'var(--gold)' }}>Pts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((s, i) => (
                      <tr key={s.teamId} onClick={() => setSelectedTeamId(s.teamId)} className="standings-row" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}>
                        <td style={{ ...td, textAlign: 'left', color: 'var(--chalk-dim)' }}>{i + 1}</td>
                        <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: 'var(--gold)' }}>{s.teamName}</td>
                        <td style={td}>{s.played}</td><td style={td}>{s.won}</td><td style={td}>{s.drawn}</td><td style={td}>{s.lost}</td>
                        <td style={td}>{s.framesFor}</td><td style={td}>{s.framesAgainst}</td>
                        <td style={{ ...td, color: s.frameDifference > 0 ? '#2ecc71' : s.frameDifference < 0 ? '#e74c3c' : 'var(--chalk-dim)' }}>{s.frameDifference > 0 ? `+${s.frameDifference}` : s.frameDifference}</td>
                        <td style={{ ...td, fontWeight: 700, color: 'var(--gold)' }}>{s.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <style>{`.standings-row:hover { background: rgba(255,255,255,0.03); }`}</style>
              </div>
            )}

            {!selectedTeamId && (
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '2rem' }}>
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <h3 style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem', fontWeight: 600 }}>
                    🏆 Top Wins
                  </h3>
                  {winLeaders === null ? (
                    <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>…</p>
                  ) : winLeaders.length === 0 ? (
                    <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>No completed matches yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {winLeaders.map((w, i) => (
                        <div key={w.playerId} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '0.45rem 0.7rem' }}>
                          <span style={{ color: 'var(--chalk-dim)', fontSize: '0.75rem', width: '16px' }}>{i + 1}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.playerName}</div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)' }}>{w.teamName}</div>
                          </div>
                          <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.15rem', color: 'var(--gold)' }}>{w.wins}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <h3 style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem', fontWeight: 600 }}>
                    🎯 Top Breaks
                  </h3>
                  {league.notableBreakThreshold == null ? (
                    <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>No threshold configured for this league.</p>
                  ) : breakLeaders === null ? (
                    <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>…</p>
                  ) : breakLeaders.length === 0 ? (
                    <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>No breaks of {league.notableBreakThreshold}+ yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {breakLeaders.map((b, i) => (
                        <div key={b._id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '0.45rem 0.7rem' }}>
                          <span style={{ color: 'var(--chalk-dim)', fontSize: '0.75rem', width: '16px' }}>{i + 1}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.playerName}</div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)' }}>{b.teamName}</div>
                          </div>
                          <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.15rem', color: '#2ecc71' }}>{b.points}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            </>
          )}

          {tab === 'fixtures' && (
            division.fixtures.length === 0 ? (
              <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>{t.noFixturesYetLabel}</p>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
                  {(['list', 'calendar'] as const).map(mode => (
                    <button
                      key={mode}
                      onClick={() => { setFixturesViewMode(mode); setCalendarSelectedDate(null); }}
                      style={{
                        background: fixturesViewMode === mode ? 'var(--gold)' : 'transparent',
                        color: fixturesViewMode === mode ? 'var(--baize-dark)' : 'var(--chalk-dim)',
                        border: `1px solid ${fixturesViewMode === mode ? 'var(--gold)' : 'rgba(255,255,255,0.15)'}`,
                        borderRadius: '6px', padding: '0.35rem 0.85rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {mode === 'list' ? '☰ List' : '🗓 Calendar'}
                    </button>
                  ))}
                </div>

                {fixturesViewMode === 'list' ? (
                  division.fixtures.map(fixtureRow)
                ) : calendarSelectedDate ? (
                  <>
                    <button onClick={() => setCalendarSelectedDate(null)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.3rem 0.65rem', fontSize: '0.75rem', cursor: 'pointer', marginBottom: '0.9rem' }}>
                      ← Back to calendar
                    </button>
                    {division.fixtures.filter(f => f.scheduledAt && isSameLocalDate(f.scheduledAt, calendarSelectedDate)).map(fixtureRow)}
                  </>
                ) : (
                  <FixtureCalendar
                    fixtures={division.fixtures}
                    onSelectDate={date => setCalendarSelectedDate(date)}
                    onOpenFixture={id => { setFixturesViewMode('list'); setExpandedFixtureId(id); }}
                  />
                )}
              </>
            )
          )}

          {tab === 'teams' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {division.teams.length === 0 && <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>{t.noTeamsYetLabel}</p>}
              {division.teams.map(team => (
                <div key={team._id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.85rem 1rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.4rem' }}>{team.name}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {team.roster.map(p => (
                      <span key={p._id} style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', background: 'rgba(255,255,255,0.04)', borderRadius: '5px', padding: '0.15rem 0.5rem' }}>
                        {team.captainPlayerId === p._id ? '⭐ ' : ''}{p.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'history' && (
            selectedSeason ? (
              <>
                <button onClick={() => setSelectedSeason(null)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.3rem 0.65rem', fontSize: '0.75rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
                  {t.backToAllSeasons}
                </button>
                <h3 style={{ fontSize: '1rem', color: 'var(--chalk)', marginBottom: '0.2rem' }}>{selectedSeason.seasonLabel}</h3>
                <p style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', marginBottom: '0.9rem' }}>
                  {t.endedLabel} {new Date(selectedSeason.endedAt).toLocaleDateString()} · ↑{selectedSeason.promotionSpots} {t.promotedLabel} / ↓{selectedSeason.relegationSpots} {t.relegatedLabel}
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <th style={{ ...th, textAlign: 'left' }}>#</th>
                        <th style={{ ...th, textAlign: 'left' }}>{t.standingsTeamCol}</th>
                        <th style={th}>P</th><th style={th}>W</th><th style={th}>D</th><th style={th}>L</th>
                        <th style={th}>F</th><th style={th}>A</th><th style={th}>+/-</th>
                        <th style={{ ...th, color: 'var(--gold)' }}>Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSeason.standings.map((s, i) => (
                        <tr key={s.teamId} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ ...td, textAlign: 'left', color: 'var(--chalk-dim)' }}>{i + 1}</td>
                          <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{s.teamName}</td>
                          <td style={td}>{s.played}</td><td style={td}>{s.won}</td><td style={td}>{s.drawn}</td><td style={td}>{s.lost}</td>
                          <td style={td}>{s.framesFor}</td><td style={td}>{s.framesAgainst}</td>
                          <td style={{ ...td, color: s.frameDifference > 0 ? '#2ecc71' : s.frameDifference < 0 ? '#e74c3c' : 'var(--chalk-dim)' }}>{s.frameDifference > 0 ? `+${s.frameDifference}` : s.frameDifference}</td>
                          <td style={{ ...td, fontWeight: 700, color: 'var(--gold)' }}>{s.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : seasons === null ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}><SnookerBallSpinner size={24} /></div>
            ) : seasons.length === 0 ? (
              <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>{t.noPastSeasonsYet}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {seasons.map(s => (
                  <div key={s._id} onClick={() => openSeason(s._id)} style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.75rem 1rem', cursor: 'pointer' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{s.seasonLabel}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginTop: '0.15rem' }}>
                        {t.endedLabel} {new Date(s.endedAt).toLocaleDateString()} · ↑{s.promotionSpots} / ↓{s.relegationSpots}
                      </div>
                    </div>
                    <span style={{ color: 'var(--chalk-dim)' }}>›</span>
                  </div>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
